import { useEffect, useState, useRef } from "react";

/**
 * HightownLanding — faithful React port of hightown_worldcup_mobile.html.
 * Phone-mockup chrome stripped (real users on real phones); all inner sections
 * preserved: sticky header, hero with HJFL badge + days-until-kickoff, countdown
 * bar, "From the league" message, How-it-works steps, animated auction widget,
 * self-hosted video, feedback CTA, footer.
 */

const KICKOFF = new Date("2026-06-11T19:00:00Z");
const HJFL_BADGE = "/hjfl-badge.jpg";
const BRAZIL_FLAG = "/brazil-flag.svg";

// GA4 funnel: page_view (auto via gtag config) + widget_seen + video_play + cta_click.
// Fires only when window.gtag exists (no-op in tests / preview without GA).
const track = (event, params = {}) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", event, params);
  }
};

function useKickoffCountdown() {
  const compute = () => {
    const diff = KICKOFF.getTime() - Date.now();
    if (diff <= 0) return { days: 0, hrs: 0, mins: 0, secs: 0 };
    return {
      days: Math.floor(diff / 86400000),
      hrs: Math.floor((diff % 86400000) / 3600000),
      mins: Math.floor((diff % 3600000) / 60000),
      secs: Math.floor((diff % 60000) / 1000),
    };
  };
  const [t, setT] = useState(compute);
  useEffect(() => {
    const i = setInterval(() => setT(compute()), 1000);
    return () => clearInterval(i);
  }, []);
  return t;
}

const WIDGET_EVENTS = [
  { at: 5, bid: "£20m", leader: "Dave", leaderColor: "var(--teal)" },
  { at: 11, bid: "£30m", leader: "Sarah", leaderColor: "var(--muted)" },
  { at: 16, bid: "£35m", leader: "Dave", leaderColor: "var(--teal)" },
];

function LiveAuctionWidget() {
  const DURATION = 19;
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [bid, setBid] = useState("£15m");
  const [leader, setLeader] = useState("Sarah");
  const [leaderColor, setLeaderColor] = useState("var(--muted)");
  const [showToast, setShowToast] = useState(false);
  const fired = useRef(new Set());
  const leaderRef = useRef("Sarah");
  const wrapRef = useRef(null);
  const seenFiredRef = useRef(false);

  // GA: fire widget_seen the first time the widget scrolls into view
  useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && !seenFiredRef.current) {
          seenFiredRef.current = true;
          track("hightown_widget_seen");
          obs.disconnect();
        }
      }
    }, { threshold: 0.4 });
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const tick = setInterval(() => {
      setSecondsElapsed((prev) => {
        const next = prev + 0.1;
        if (next >= DURATION) {
          setShowToast(true);
          setTimeout(() => {
            setShowToast(false);
            setBid("£15m");
            setLeader("Sarah");
            leaderRef.current = "Sarah";
            setLeaderColor("var(--muted)");
            fired.current.clear();
          }, 2200);
          return 0;
        }
        for (const ev of WIDGET_EVENTS) {
          if (!fired.current.has(ev.at) && next >= ev.at) {
            fired.current.add(ev.at);
            setBid(ev.bid);
            setLeader(ev.leader);
            leaderRef.current = ev.leader;
            setLeaderColor(ev.leaderColor);
          }
        }
        return next;
      });
    }, 100);
    return () => clearInterval(tick);
  }, []);

  const remaining = Math.max(0, Math.ceil(DURATION - secondsElapsed));
  const pct = Math.max(0, 100 - (secondsElapsed / DURATION) * 100);

  return (
    <div className="hl-widget-wrap" data-testid="hightown-auction-widget" ref={wrapRef}>
      <div className="hl-widget-top">
        <div className="hl-w-timer">00:{String(remaining).padStart(2, "0")}</div>
        <div className="hl-live-badge"><span className="hl-live-dot" /> LIVE</div>
      </div>
      <img src={BRAZIL_FLAG} alt="Brazil" style={{ width: 56, height: 80, objectFit: "contain", marginBottom: 8, display: "block" }} />
      <div className="hl-w-name">Brazil</div>
      <div className="hl-w-meta">GROUP STAGE · WORLD CUP 2026</div>
      <div className="hl-w-bid-label">Current highest bid</div>
      <div className="hl-w-bid">{bid}</div>
      <div className="hl-w-leader" style={{ color: leaderColor }}>{leader} leading</div>
      <div className="hl-w-bar"><div className="hl-w-bar-fill" style={{ width: `${pct}%` }} /></div>
      <div className="hl-w-btns">
        {["+£1m", "+£5m", "+£10m", "+£20m", "+£50m"].map((l) => (
          <div key={l} className="hl-w-btn">{l}</div>
        ))}
        <div className="hl-w-btn hl-pass">Pass</div>
      </div>
      <div className="hl-w-players-row">
        <div className="hl-w-avs">
          {[
            { i: "DA", bg: "#1ec8e0" },
            { i: "SA", bg: "#4ade80" },
            { i: "JO", bg: "#f5c842" },
            { i: "MK", bg: "#f97316" },
            { i: "LE", bg: "#c084fc" },
          ].map((a) => (
            <div key={a.i} className="hl-w-av" style={{ background: a.bg }}>{a.i}</div>
          ))}
        </div>
        <span className="hl-w-av-text">5 friends · from the touchline</span>
      </div>
      <div className="hl-w-caption">Live auction · 30 seconds per team</div>
      <div className={`hl-w-toast ${showToast ? "show" : ""}`}>🏆 Brazil — Won by {leader}</div>
    </div>
  );
}

export default function HightownLanding() {
  const { days, hrs, mins, secs } = useKickoffCountdown();

  const pageViewFired = useRef(false);
  useEffect(() => {
    document.title = "Hightown JFL × Sport X — World Cup 2026";
    if (!pageViewFired.current) {
      pageViewFired.current = true;
      track("hightown_page_view");
    }
    return () => { document.title = "Sport X"; };
  }, []);

  const steps = [
    { n: "01", t: "Create or join", b: "Set up a group with family or friends. Share a link. Everyone joins in seconds." },
    { n: "02", t: "Run the auction", b: "Everyone bids on real World Cup teams with a virtual budget. 30 seconds per team. Who bids highest wins them." },
    { n: "03", t: "Follow the World Cup", b: "Your teams score points in real matches. The leaderboard moves with every game. No tinkering needed." },
    { n: "04", t: "Win something", b: "Best squad wins. A portion of every entry goes to a cause the winner chooses." },
  ];

  return (
    <div className="hl-root" data-testid="hightown-landing-page">
      <style>{HL_CSS}</style>
      <div className="hl-shell">

        {/* Header */}
        <div className="hl-header">
          <div className="hl-header-left">
            <img src={HJFL_BADGE} className="hl-header-badge" alt="Hightown JFL" />
            <div className="hl-header-name">Hightown JFL <span>Est. 1997</span></div>
          </div>
          <a href="https://app.mysportx.org" className="hl-sportx-link" target="_blank" rel="noopener">
            <div className="hl-sportx-tag">SPORT X</div>
          </a>
        </div>

        {/* Hero */}
        <div className="hl-hero">
          <div className="hl-hero-badge-wrap">
            <div className="hl-hero-badge-glow" />
            <img src={HJFL_BADGE} className="hl-hero-badge" alt="Hightown JFL" />
          </div>
          <div className="hl-hero-from">A message from Hightown JFL</div>
          <h1 className="hl-h1">THE WORLD CUP<br />STARTS IN<br /><span className="hl-teal">{days}</span> DAYS.</h1>
          <p className="hl-hero-sub">We found something we think you'll love. Bid on real World Cup teams. Compete with the Hightown community. Follow every match together.</p>
          <p className="hl-hero-note">Free to play. No gambling. No app to download.</p>
          <div className="hl-hero-pills">
            <div className="hl-pill hl-pill-good">Free to play</div>
            <div className="hl-pill hl-pill-good">Live auctions</div>
            <div className="hl-pill hl-pill-good">Your friends</div>
            <div className="hl-pill hl-pill-no">No gambling</div>
            <div className="hl-pill hl-pill-no">No subscription</div>
          </div>

        </div>

        {/* Countdown */}
        <div className="hl-countdown-bar">
          <span className="hl-cd-label">World Cup 2026 kicks off</span>
          <div className="hl-cd-units">
            <div className="hl-cd-unit"><span className="hl-cd-num">{String(days).padStart(2,"0")}</span><span className="hl-cd-sub">Days</span></div>
            <span className="hl-cd-sep">:</span>
            <div className="hl-cd-unit"><span className="hl-cd-num">{String(hrs).padStart(2,"0")}</span><span className="hl-cd-sub">Hrs</span></div>
            <span className="hl-cd-sep">:</span>
            <div className="hl-cd-unit"><span className="hl-cd-num">{String(mins).padStart(2,"0")}</span><span className="hl-cd-sub">Mins</span></div>
            <span className="hl-cd-sep">:</span>
            <div className="hl-cd-unit"><span className="hl-cd-num">{String(secs).padStart(2,"0")}</span><span className="hl-cd-sub">Secs</span></div>
          </div>
          <span className="hl-cd-date">11 June 2026 · USA</span>
        </div>

        <hr className="hl-hr" />

        {/* From the league message */}
        <div className="hl-section">
          <div className="hl-section-label">From the league</div>
          <div className="hl-section-title">We want to try something new.</div>
          <div className="hl-message-block">
            <p>We've been looking at ways to bring the Hightown JFL community together beyond match days — and the World Cup feels like the perfect moment.</p>
            <p><strong>Sport X is a free auction game.</strong> You bid on real World Cup teams with a virtual budget, back your team, then follow the tournament as they score points in real matches.</p>
            <p>No gambling. No money involved. No subscription. Just a bit of fun — and if you enjoy it, we'll think about using it for fundraising and our own league competitions.</p>
            <p><strong>Try it and tell us what you think.</strong></p>
            <div className="hl-message-sig">
              <img src={HJFL_BADGE} alt="" />
              Hightown Junior Football League
            </div>
          </div>
        </div>

        <hr className="hl-hr" />

        {/* How it works */}
        <div className="hl-section">
          <div className="hl-section-label">How it works</div>
          <div className="hl-section-title">Three steps. Then just watch.</div>
          <div className="hl-steps">
            {steps.map((s) => (
              <div key={s.n} className="hl-step">
                <div className="hl-step-num">{s.n}</div>
                <div className="hl-step-title">{s.t}</div>
                <div className="hl-step-body">{s.b}</div>
              </div>
            ))}
          </div>
          <LiveAuctionWidget />
        </div>

        {/* Video */}
        <div className="hl-section">
          <div className="hl-section-label">See it in action</div>
          <div className="hl-section-title">Watch how it works.</div>
          <div className="hl-video-wrap">
            <video
              src="/hightown.mp4"
              controls
              playsInline
              preload="metadata"
              className="hl-video"
              data-testid="hightown-video"
              onPlay={(e) => {
                // Fire only on first play to avoid spam from pause/resume
                if (!e.currentTarget.dataset.gaFired) {
                  e.currentTarget.dataset.gaFired = "1";
                  track("hightown_video_play");
                }
              }}
            >
              Sorry, your browser doesn't support embedded videos.
            </video>
          </div>
        </div>

        <hr className="hl-hr" />

        {/* Feedback */}
        <div className="hl-feedback">
          <div className="hl-feedback-title">Try it. Then<br /><span>tell us what you think.</span></div>
          <p className="hl-feedback-sub">Your feedback goes directly back to the league. If enough of you enjoy it, we'll use it for fundraising and our own competitions next season.</p>
          <a href="https://app.mysportx.org" className="hl-btn-primary" data-testid="hightown-cta-play" target="_blank" rel="noopener" onClick={() => track("hightown_cta_click", { cta: "footer_play_now" })}>Play now →</a>
          <a href="mailto:hightownjfl@outlook.com" className="hl-btn-secondary" data-testid="hightown-cta-feedback" onClick={() => track("hightown_cta_click", { cta: "send_feedback" })}>Send feedback</a>
          <p className="hl-feedback-contact">Questions? <a href="mailto:hightownjfl@outlook.com">hightownjfl@outlook.com</a></p>
        </div>

        {/* Footer */}
        <div className="hl-footer">
          <div className="hl-footer-left">
            <img src={HJFL_BADGE} className="hl-footer-badge" alt="" />
            <span className="hl-footer-text">Hightown JFL · Est. 1997</span>
          </div>
          <span className="hl-footer-sportx">SPORT X</span>
        </div>

      </div>
    </div>
  );
}

const HL_CSS = `
.hl-root {
  --navy: #0d1c35;
  --navy-light: #142240;
  --navy-mid: #1a2d52;
  --teal: #1ec8e0;
  --teal-dim: rgba(30,200,224,0.1);
  --teal-border: rgba(30,200,224,0.3);
  --red: #ff4d6a;
  --gold: #f5c842;
  --white: #ffffff;
  --offwhite: #e8edf4;
  --muted: #7a9ab8;
  --muted2: #a8bdd0;
  --border: rgba(255,255,255,0.08);
  background: var(--navy);
  color: var(--white);
  font-family: 'Barlow', system-ui, -apple-system, sans-serif;
  -webkit-font-smoothing: antialiased;
  min-height: 100vh;
}
.hl-root *, .hl-root *::before, .hl-root *::after { box-sizing: border-box; }
.hl-shell { max-width: 480px; margin: 0 auto; }

/* Header */
.hl-header {
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(13,28,53,0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border);
  position: sticky;
  top: 0;
  z-index: 100;
}
.hl-header-left { display: flex; align-items: center; gap: 10px; }
.hl-header-badge { width: 32px; height: 32px; object-fit: contain; border-radius: 4px; }
.hl-header-name {
  font-family: "Barlow Condensed", sans-serif;
  font-weight: 700; font-size: 13px; letter-spacing: 1px;
  text-transform: uppercase; color: white; line-height: 1.1;
}
.hl-header-name span {
  display: block; font-size: 9px; font-weight: 400;
  color: var(--muted); letter-spacing: 1.5px;
}
.hl-sportx-link { text-decoration: none; text-align: center; }
.hl-sportx-tag {
  font-family: "Barlow Condensed", sans-serif;
  font-weight: 700; font-size: 11px; letter-spacing: 1.5px;
  text-transform: uppercase; color: var(--teal);
  border: 1px solid var(--teal-border);
  padding: 5px 10px; border-radius: 4px;
}
.hl-sportx-sub {
  font-family: "Barlow Condensed", sans-serif;
  font-size: 9px; font-weight: 600; letter-spacing: 1.5px;
  text-transform: uppercase; color: var(--teal); margin-top: 4px;
}

/* Hero */
.hl-hero { padding: 36px 24px 28px; text-align: center; position: relative; overflow: hidden; }
.hl-hero::before {
  content: ""; position: absolute; inset: 0;
  background: radial-gradient(ellipse 100% 60% at 50% 0%, rgba(30,200,224,0.07) 0%, transparent 70%);
  pointer-events: none;
}
.hl-hero-badge-wrap { position: relative; width: 180px; height: 180px; margin: 0 auto 20px; }
.hl-hero-badge { width: 180px; height: 180px; object-fit: contain; position: relative; z-index: 1; border-radius: 12px; }
.hl-hero-badge-glow {
  position: absolute; inset: -16px;
  background: radial-gradient(circle, rgba(30,200,224,0.18) 0%, transparent 70%);
  border-radius: 50%;
}
.hl-hero-from {
  font-family: "Barlow Condensed", sans-serif;
  font-size: 14px; font-weight: 600; letter-spacing: 2.5px;
  text-transform: uppercase; color: var(--teal); margin-bottom: 14px;
  position: relative; z-index: 2;
}
.hl-h1 {
  font-family: "Barlow Condensed", sans-serif;
  font-weight: 900; font-size: 52px; line-height: 0.9;
  text-transform: uppercase; letter-spacing: -0.5px;
  color: white; margin: 0 0 16px;
  position: relative; z-index: 2;
}
.hl-teal { color: var(--teal); }
.hl-hero-sub {
  font-size: 14px; color: var(--muted2); margin: 0 0 10px;
  font-weight: 300; line-height: 1.55; position: relative; z-index: 2;
}
.hl-hero-note { font-size: 11px; color: var(--muted); margin: 0 0 20px; position: relative; z-index: 2; }
.hl-hero-pills {
  display: flex; justify-content: center; gap: 6px; flex-wrap: wrap;
  margin-bottom: 24px; position: relative; z-index: 2;
}
.hl-pill {
  border: 1px solid var(--border); color: var(--muted2);
  padding: 5px 12px; border-radius: 100px;
  font-size: 10px; font-weight: 500;
  font-family: "Barlow Condensed", sans-serif;
  text-transform: uppercase; letter-spacing: 1px;
}
.hl-pill-good { border-color: var(--teal-border); color: var(--teal); background: var(--teal-dim); }
.hl-pill-no { border-color: rgba(255,77,106,0.25); color: #ff8099; background: rgba(255,77,106,0.06); }
.hl-hero-cta {
  display: block; background: var(--teal); color: var(--navy);
  padding: 16px; border-radius: 8px;
  font-family: "Barlow Condensed", sans-serif;
  font-weight: 800; font-size: 16px; letter-spacing: 2px;
  text-transform: uppercase; text-decoration: none;
  margin-bottom: 10px; position: relative; z-index: 2;
}
.hl-hero-cta-sub { font-size: 10px; color: var(--muted); letter-spacing: 0.3px; position: relative; z-index: 2; }

/* Countdown */
.hl-countdown-bar {
  background: var(--navy-light);
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  padding: 14px 20px;
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 8px;
}
.hl-cd-label {
  font-family: "Barlow Condensed", sans-serif;
  font-size: 10px; font-weight: 600; letter-spacing: 2px;
  text-transform: uppercase; color: var(--muted);
}
.hl-cd-units { display: flex; gap: 4px; align-items: center; }
.hl-cd-unit { text-align: center; }
.hl-cd-num {
  font-family: "Barlow Condensed", sans-serif;
  font-weight: 800; font-size: 26px; color: var(--teal);
  line-height: 1; display: block; min-width: 36px;
}
.hl-cd-sub {
  font-size: 8px; font-weight: 600; letter-spacing: 1.5px;
  text-transform: uppercase; color: var(--muted);
}
.hl-cd-sep {
  font-family: "Barlow Condensed", sans-serif;
  font-size: 22px; font-weight: 800; color: var(--navy-mid);
  margin-bottom: 10px;
}
.hl-cd-date {
  font-family: "Barlow Condensed", sans-serif;
  font-size: 10px; font-weight: 500; color: var(--muted);
  letter-spacing: 1px; width: 100%;
}

/* Section */
.hl-section { padding: 32px 20px; }
.hl-section-label {
  font-family: "Barlow Condensed", sans-serif;
  font-size: 10px; font-weight: 700; letter-spacing: 2.5px;
  text-transform: uppercase; color: var(--teal); margin-bottom: 8px;
}
.hl-section-title {
  font-family: "Barlow Condensed", sans-serif;
  font-weight: 800; font-size: 32px;
  text-transform: uppercase; color: white;
  line-height: 1; margin-bottom: 20px; letter-spacing: -0.3px;
}

.hl-hr { border: none; border-top: 1px solid var(--border); margin: 0; }

/* Message */
.hl-message-block {
  background: var(--navy-mid);
  border: 1px solid var(--border);
  border-left: 3px solid var(--teal);
  border-radius: 0 10px 10px 0;
  padding: 20px;
}
.hl-message-block p {
  font-size: 13px; color: var(--offwhite); line-height: 1.65;
  font-weight: 300; margin: 0 0 10px;
}
.hl-message-block p:last-of-type { margin-bottom: 0; }
.hl-message-block strong { color: white; font-weight: 600; }
.hl-message-sig {
  font-family: "Barlow Condensed", sans-serif;
  font-size: 11px; font-weight: 600; letter-spacing: 1.5px;
  text-transform: uppercase; color: var(--teal);
  margin-top: 16px; display: flex; align-items: center; gap: 8px;
}
.hl-message-sig img { width: 22px; height: 22px; object-fit: contain; opacity: 0.8; border-radius: 4px; }

/* Steps */
.hl-steps {
  display: flex; flex-direction: column; gap: 1px;
  background: var(--border);
  border: 1px solid var(--border);
  border-radius: 10px; overflow: hidden;
  margin-bottom: 24px;
}
.hl-step { background: var(--navy-light); padding: 18px 16px; position: relative; }
.hl-step-num {
  font-family: "Barlow Condensed", sans-serif;
  font-size: 40px; font-weight: 900;
  color: rgba(30,200,224,0.10); line-height: 1; margin-bottom: 6px;
}
.hl-step-title {
  font-family: "Barlow Condensed", sans-serif;
  font-size: 12px; font-weight: 700; color: white;
  letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 4px;
}
.hl-step-body { font-size: 12px; color: var(--muted2); line-height: 1.5; font-weight: 300; }

/* Widget */
.hl-widget-wrap {
  background: var(--navy-light);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 20px; position: relative; overflow: hidden;
  margin-bottom: 0;
}
.hl-widget-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
.hl-w-timer {
  font-family: "Barlow Condensed", sans-serif;
  font-size: 56px; font-weight: 900; color: var(--red);
  line-height: 1; letter-spacing: 2px;
}
.hl-live-badge {
  background: var(--red); color: white;
  padding: 4px 8px; border-radius: 4px;
  font-family: "Barlow Condensed", sans-serif;
  font-size: 10px; font-weight: 700; letter-spacing: 1px;
  display: flex; align-items: center; gap: 4px;
}
.hl-live-dot {
  width: 4px; height: 4px; border-radius: 50%; background: white;
  animation: hlPulse 1s ease-in-out infinite;
}
@keyframes hlPulse { 0%,100%{opacity:1} 50%{opacity:0.2} }
.hl-w-name {
  font-family: "Barlow Condensed", sans-serif;
  font-size: 24px; font-weight: 800; color: white;
  text-transform: uppercase; line-height: 1; margin-bottom: 2px;
}
.hl-w-meta {
  font-family: "Barlow Condensed", sans-serif;
  font-size: 10px; font-weight: 600; letter-spacing: 1.5px;
  color: var(--muted); text-transform: uppercase; margin-bottom: 12px;
}
.hl-w-bid-label {
  font-family: "Barlow Condensed", sans-serif;
  font-size: 9px; font-weight: 700; letter-spacing: 2px;
  color: var(--teal); text-transform: uppercase; margin-bottom: 2px;
}
.hl-w-bid {
  font-family: "Barlow Condensed", sans-serif;
  font-size: 36px; font-weight: 900; color: var(--teal);
  line-height: 1; margin-bottom: 2px;
}
.hl-w-leader { font-size: 11px; color: var(--muted); margin-bottom: 12px; }
.hl-w-bar { height: 2px; background: rgba(255,77,106,0.15); border-radius: 1px; margin-bottom: 12px; overflow: hidden; }
.hl-w-bar-fill { height: 100%; background: var(--red); border-radius: 1px; box-shadow: 0 0 6px rgba(255,77,106,0.5); transition: width 0.1s linear; }
.hl-w-btns { display: grid; grid-template-columns: repeat(3,1fr); gap: 5px; margin-bottom: 12px; }
.hl-w-btn {
  background: rgba(30,200,224,0.07);
  border: 1px solid rgba(30,200,224,0.2);
  color: var(--teal); padding: 8px; border-radius: 6px;
  font-family: "Barlow Condensed", sans-serif;
  font-size: 12px; font-weight: 700; text-align: center;
}
.hl-w-btn.hl-pass {
  background: rgba(255,77,106,0.06);
  border-color: rgba(255,77,106,0.2);
  color: #ff8099;
}
.hl-w-players-row { display: flex; align-items: center; gap: 5px; }
.hl-w-avs { display: flex; }
.hl-w-av {
  width: 20px; height: 20px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 7px; font-weight: 800; color: var(--navy);
  margin-left: -5px; border: 2px solid var(--navy-light);
  font-family: "Barlow Condensed", sans-serif;
}
.hl-w-av:first-child { margin-left: 0; }
.hl-w-av-text { font-size: 10px; color: var(--muted); margin-left: 5px; font-weight: 300; }
.hl-w-caption { text-align: center; font-size: 10px; color: var(--muted); margin-top: 12px; letter-spacing: 0.3px; }
.hl-w-toast {
  position: absolute; bottom: 14px; left: 50%;
  transform: translateX(-50%) translateY(14px);
  background: var(--teal); color: var(--navy);
  padding: 8px 16px; border-radius: 100px;
  font-family: "Barlow Condensed", sans-serif;
  font-size: 13px; font-weight: 800; letter-spacing: 1px;
  text-transform: uppercase; white-space: nowrap;
  opacity: 0; transition: opacity 0.3s, transform 0.3s;
  pointer-events: none;
}
.hl-w-toast.show { opacity: 1; transform: translateX(-50%) translateY(0); }

/* Video */
.hl-video-wrap {
  border-radius: 12px; overflow: hidden;
  background: #000; border: 1px solid rgba(30,200,224,0.2);
}
.hl-video { width: 100%; display: block; max-height: 540px; object-fit: contain; background: #000; }

/* Feedback */
.hl-feedback {
  background: var(--navy-light);
  border-top: 1px solid var(--border);
  padding: 32px 20px; text-align: center;
}
.hl-feedback-title {
  font-family: "Barlow Condensed", sans-serif;
  font-weight: 900; font-size: 30px;
  text-transform: uppercase; color: white;
  line-height: 1; margin-bottom: 12px;
}
.hl-feedback-title span { color: var(--teal); }
.hl-feedback-sub { font-size: 13px; color: var(--muted2); margin-bottom: 20px; font-weight: 300; line-height: 1.55; }
.hl-btn-primary {
  display: block; background: var(--teal); color: var(--navy);
  padding: 15px; border-radius: 8px;
  font-family: "Barlow Condensed", sans-serif;
  font-weight: 800; font-size: 15px; letter-spacing: 2px;
  text-transform: uppercase; text-decoration: none; margin-bottom: 8px;
}
.hl-btn-secondary {
  display: block; background: transparent; color: var(--muted2);
  border: 1px solid var(--border); padding: 14px; border-radius: 8px;
  font-family: "Barlow Condensed", sans-serif;
  font-weight: 600; font-size: 14px; letter-spacing: 1.5px;
  text-transform: uppercase; text-decoration: none; margin-bottom: 16px;
}
.hl-feedback-contact { font-size: 11px; color: var(--muted); }
.hl-feedback-contact a { color: var(--teal); text-decoration: none; }

/* Footer */
.hl-footer {
  padding: 16px 20px; border-top: 1px solid var(--border);
  display: flex; justify-content: space-between; align-items: center;
}
.hl-footer-left { display: flex; align-items: center; gap: 8px; }
.hl-footer-badge { width: 20px; height: 20px; object-fit: contain; opacity: 0.6; border-radius: 3px; }
.hl-footer-text {
  font-family: "Barlow Condensed", sans-serif;
  font-size: 11px; letter-spacing: 1px; text-transform: uppercase;
  color: var(--muted);
}
.hl-footer-sportx {
  font-family: "Barlow Condensed", sans-serif;
  font-weight: 700; font-size: 11px; letter-spacing: 1.5px; color: var(--teal);
}
`;
